import { Button, ButtonGroup } from "reactstrap";
import classNames from "classnames";
import React, { useEffect, useRef } from "react";
import { ThunkDispatch } from "redux-thunk";

import { EditorState, World } from "../../../types";
import { Actions } from "../../actions";
import {
  advanceGameState,
  advancePlaybackGameState,
  rewindAllGameState,
  stepBackGameState,
} from "../../actions/stage-actions";
import { updatePlaybackState } from "../../actions/ui-actions";
import { SPEED_OPTIONS } from "../../constants/constants";
import { forgivingPress } from "../../utils/pointer";
import TickClock from "./tick-clock";

interface StageControlsProps {
  readonly?: boolean;
  world: World;
  // Playback ticks dispatch a thunk, so this needs to be the store's dispatch
  // rather than redux's plain Dispatch<AnyAction>.
  dispatch: ThunkDispatch<EditorState, undefined, Actions>;
  speed: number;
  running: boolean;
  runningDirection: "forward" | "rewind";
}

const StageControls: React.FC<StageControlsProps> = ({
  readonly,
  world,
  dispatch,
  speed,
  running,
  runningDirection,
}) => {
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timerSpeedRef = useRef<number | null>(null);

  const rewinding = running && runningDirection === "rewind";

  const onTick = () => {
    if (runningDirection === "rewind") {
      dispatch(stepBackGameState(world.id));
    } else {
      dispatch(advancePlaybackGameState(world.id));
    }
  };

  useEffect(() => {
    if (running && (!timerRef.current || timerSpeedRef.current !== speed)) {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      timerSpeedRef.current = speed;
      timerRef.current = setInterval(onTick, speed);
    } else if (!running && timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running, runningDirection, speed, world.id]);

  // Auto-stop rewinding when history is exhausted
  useEffect(() => {
    if (rewinding && world.history?.length === 0) {
      dispatch(updatePlaybackState({ speed, running: false }));
    }
  }, [dispatch, rewinding, speed, world.history?.length]);

  return (
    <div className="stage-controls">
      <div className="left" style={{ width: 122 }} />

      <div style={{ flex: 1 }} />

      <div className={classNames("center transport-controls", { "is-playing": running })} data-tutorial-id="controls">
        {/* Reset - black like the clock, distinct from rewind: jumps back fast and stops */}
        {!readonly && (
          <Button
            className="transport-btn transport-black"
            disabled={world.history && world.history.length === 0}
            {...forgivingPress(() => dispatch(rewindAllGameState(world.id)), { fireOn: "press" })}
          >
            <svg width="18" height="18" viewBox="0 0 14 14" fill="currentColor">
              <rect x="0" y="1" width="2.5" height="12" />
              <polygon points="8,1 8,13 3,7" />
              <polygon points="13,1 13,13 8,7" />
            </svg>
          </Button>
        )}
        {/* Rewind - green, continuous backward play */}
        {!readonly && (
          <Button
            className={classNames("transport-btn transport-green", { selected: rewinding })}
            disabled={!rewinding && world.history && world.history.length === 0}
            {...forgivingPress(
              () =>
                dispatch(
                  rewinding
                    ? updatePlaybackState({ speed, running: false })
                    : updatePlaybackState({ speed, running: true, runningDirection: "rewind" }),
                ),
              { fireOn: "press" },
            )}
          >
            <svg width="16" height="18" viewBox="0 0 12 14" fill="currentColor">
              <polygon points="12,1 12,13 0,7" />
            </svg>
          </Button>
        )}
        {/* Step Backward - yellow, one tick back */}
        {!readonly && (
          <Button
            className="transport-btn transport-yellow"
            disabled={world.history && world.history.length === 0}
            {...forgivingPress(() => dispatch(stepBackGameState(world.id)), { fireOn: "press" })}
          >
            <svg width="18" height="18" viewBox="0 0 14 14" fill="currentColor">
              <polygon points="12,1 12,13 2,7" />
              <rect x="0" y="1" width="2.5" height="12" />
            </svg>
          </Button>
        )}
        {/* Stop - red, center */}
        <Button
          className={classNames("transport-btn transport-red", { selected: !running })}
          {...forgivingPress(() => dispatch(updatePlaybackState({ speed, running: false })), {
            fireOn: "press",
          })}
        >
          <svg width="14" height="14" viewBox="0 0 12 12" fill="currentColor">
            <rect x="0" y="0" width="12" height="12" rx="1" />
          </svg>
        </Button>
        {/* Step Forward - yellow, one tick forward */}
        {!readonly && (
          <Button
            className="transport-btn transport-yellow"
            {...forgivingPress(() => dispatch(advanceGameState(world.id)), { fireOn: "press" })}
          >
            <svg width="18" height="18" viewBox="0 0 14 14" fill="currentColor">
              <polygon points="2,1 2,13 12,7" />
              <rect x="11.5" y="1" width="2.5" height="12" />
            </svg>
          </Button>
        )}
        {/* Play - green, continuous forward play */}
        <Button
          data-tutorial-id="play"
          className={classNames("transport-btn transport-green", {
            selected: running && runningDirection === "forward",
          })}
          {...forgivingPress(
            () =>
              dispatch(updatePlaybackState({ speed, running: true, runningDirection: "forward" })),
            { fireOn: "press" },
          )}
        >
          <svg width="16" height="18" viewBox="0 0 12 14" fill="currentColor">
            <polygon points="0,1 0,13 12,7" />
          </svg>
        </Button>
        {/* Clock */}
        <TickClock
          running={running}
          speed={speed}
          tickFrames={world.evaluatedTickFrames}
          historyLength={world.history?.length ?? 0}
        />
      </div>

      <div style={{ flex: 1 }} />

      <div className="right" style={{ width: 122 }}>
        <ButtonGroup>
          {Object.keys(SPEED_OPTIONS).map((name) => (
            <Button
              size="sm"
              key={name}
              style={{ minWidth: 0, fontSize: "1.2rem", padding: "0 8px" }}
              className={classNames({
                selected: SPEED_OPTIONS[name as keyof typeof SPEED_OPTIONS] === speed,
              })}
              {...forgivingPress(
                () =>
                  dispatch(
                    updatePlaybackState({
                      speed: SPEED_OPTIONS[name as keyof typeof SPEED_OPTIONS],
                      running,
                    }),
                  ),
                { fireOn: "press" },
              )}
            >
              {name === "Slow" ? "🐢" : name === "Super" ? "🐇" : "•"}
            </Button>
          ))}
        </ButtonGroup>
      </div>
    </div>
  );
};

export default StageControls;
